import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';

import {apiFarmerLinkedAccounts, apiGetUser} from 'constants/Api';
import {EGPAY_ACCOUNT_TYPE} from 'constants/interfaces';
import type {
  IFdApplicationType,
  ILinkedGpayAccount,
} from 'constants/interfaces';
import {
  apiGetSpendCategories,
  apiGetWalletCashflows,
  apiLinkFdApplicationToWallet,
} from 'constants/walletCashflowApi';
import type {IWalletCashflow} from 'constants/walletCashflowTypes';
import {lshBuildApprovedCashflowDraft} from '../utils/approvedCashflowDraft';
import type {IApprovedCashflowDraft} from '../utils/approvedCashflowDraft';

interface ApplicationApprovedCashflowCardProps {
  application: IFdApplicationType;
  nominatedWalletId?: string;
  onLinked?: () => void;
}

interface ApplicationApprovedCashflowReviewDialogProps {
  application: IFdApplicationType;
  open: boolean;
  defaultWalletId?: string;
  defaultWalletOption?: TApplicationWalletOption;
  lockWalletSelection?: boolean;
  confirmLabel?: string;
  onClose: () => void;
  onPublished?: (walletId: string) => void | Promise<void>;
}

type TApplicationWalletOption = {
  id: string;
  name: string;
  accountName?: string;
  accountNumber?: string;
  availableBalance?: number;
};

type TApprovedCashflowTableRow = {
  id: string;
  label: string;
  total: number;
  values: Record<string, number>;
};

type TApprovedCashflowResultDialog = {
  type: 'success' | 'error';
  title: string;
  message: string;
  walletId?: string;
};

const APPROVED_STATES = new Set([
  'PENDING_PAYOUT',
  'FINAL_STAGE',
  'CONDITION_PRECEDENT',
  'TERM_SHEET_SIGNATURE',
  'PENDING_SIGNATURES',
]);

function lshFormatMoney(value: unknown): string {
  return Number(value || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function lshErrorMessage(error: any, fallback: string): string {
  if (Array.isArray(error)) return error.join(', ');
  return error?.message || fallback;
}

function lshApplicationApplicantUserId(
  application: IFdApplicationType,
): string | undefined {
  return (
    application.applyingUser?.externalId ||
    application.applyingUser?.id ||
    application.user?.externalId ||
    application.user?.id
  );
}

function lshApplicationLinkedWalletId(
  application: IFdApplicationType,
): string | undefined {
  if (application.applicationWallet)
    return String(application.applicationWallet);
  if (application.applicationWalletAccount?.id) {
    return String(application.applicationWalletAccount.id);
  }
  return undefined;
}

function lshUnwrapLinkedAccounts(response: any): ILinkedGpayAccount[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.getFarmerInputsLinkedAccounts)) {
    return response.getFarmerInputsLinkedAccounts;
  }
  if (Array.isArray(response?.data?.getFarmerInputsLinkedAccounts)) {
    return response.data.getFarmerInputsLinkedAccounts;
  }
  return [];
}

function lshGetWalletPaymentAccount(wallet: ILinkedGpayAccount) {
  return (
    wallet.accounts?.find(
      (account) => account.accountType === EGPAY_ACCOUNT_TYPE.TPFM,
    ) || wallet.accounts?.[0]
  );
}

function lshBuildWalletOption(
  wallet: ILinkedGpayAccount,
): TApplicationWalletOption | null {
  const account = lshGetWalletPaymentAccount(wallet);
  if (!wallet.id) return null;

  return {
    id: String(wallet.id),
    name: wallet.company?.name || account?.accountName || 'Wallet',
    accountName: account?.accountName,
    accountNumber: account?.accountNumber,
    availableBalance: Number(account?.AvailableBalance || 0),
  };
}

function lshBuildApplicationWalletOption(
  application: IFdApplicationType,
): TApplicationWalletOption | null {
  const linkedWallet = application.applicationWalletAccount;
  const account = linkedWallet?.accounts?.[0];
  const id = application.applicationWallet || linkedWallet?.id;

  if (!id) return null;

  return {
    id: String(id),
    name: linkedWallet?.customName || account?.accountName || 'Linked wallet',
    accountName: account?.accountName,
    accountNumber: account?.accountNumber,
    availableBalance: Number(account?.AvailableBalance || 0),
  };
}

function lshMergeWalletOptions(
  options: TApplicationWalletOption[],
  fallbackOption: TApplicationWalletOption | null,
): TApplicationWalletOption[] {
  const merged = fallbackOption ? [fallbackOption, ...options] : options;
  const seen = new Set<string>();

  return merged.filter((option) => {
    if (seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
}

function lshDraftHasBudgetLines(draft?: IApprovedCashflowDraft): boolean {
  return Boolean(draft?.linesByMonth.some((month) => month.lines.length > 0));
}

async function lshLoadApplicationWalletOptions(
  application: IFdApplicationType,
): Promise<TApplicationWalletOption[]> {
  const fallbackOption = lshBuildApplicationWalletOption(application);
  const userId = lshApplicationApplicantUserId(application);

  if (!userId) return lshMergeWalletOptions([], fallbackOption);

  const user = await apiGetUser(String(userId));
  const farmerId = user?.farmer?.id;
  if (!farmerId) return lshMergeWalletOptions([], fallbackOption);

  const linkedWallets = lshUnwrapLinkedAccounts(
    await apiFarmerLinkedAccounts(String(farmerId)),
  );

  return lshMergeWalletOptions(
    linkedWallets
      .map(lshBuildWalletOption)
      .filter((wallet): wallet is TApplicationWalletOption => wallet !== null),
    fallbackOption,
  );
}

async function lshPublishApprovedCashflow(
  application: IFdApplicationType,
  walletId: string,
) {
  return apiLinkFdApplicationToWallet(String(application.id), Number(walletId));
}

export function ApplicationApprovedCashflowCard(
  props: ApplicationApprovedCashflowCardProps,
) {
  const {application, nominatedWalletId, onLinked} = props;
  const [walletId, setWalletId] = useState(nominatedWalletId || '');
  const [walletOptions, setWalletOptions] = useState<
    TApplicationWalletOption[]
  >([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [cashflows, setCashflows] = useState<IWalletCashflow[]>([]);
  const [loadingCashflows, setLoadingCashflows] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [message, setMessage] = useState('');

  const isApprovedStage = APPROVED_STATES.has(application.applicationState);
  const selectedWallet = useMemo(
    () => walletOptions.find((wallet) => wallet.id === walletId),
    [walletId, walletOptions],
  );

  const loadCashflows = (nextWalletId = walletId) => {
    if (!nextWalletId) {
      setCashflows([]);
      return;
    }

    setLoadingCashflows(true);
    apiGetWalletCashflows(nextWalletId)
      .then((rows) => setCashflows(rows || []))
      .catch((err) => {
        console.error(err);
        setCashflows([]);
      })
      .finally(() => setLoadingCashflows(false));
  };

  useEffect(() => {
    let isMounted = true;

    setLoadingWallets(true);
    lshLoadApplicationWalletOptions(application)
      .then((options) => {
        if (!isMounted) return;
        setWalletOptions(options);
      })
      .catch((err) => {
        console.error('Error loading application wallets', err);
        if (isMounted) setWalletOptions([]);
      })
      .finally(() => {
        if (isMounted) setLoadingWallets(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    application,
    application.applicationWallet,
    application.applicationWalletAccount,
    application.applyingUser?.externalId,
    application.applyingUser?.id,
    application.user?.externalId,
    application.user?.id,
  ]);

  useEffect(() => {
    if (application.applicationWallet) {
      setWalletId(String(application.applicationWallet));
    } else if (nominatedWalletId) {
      setWalletId(nominatedWalletId);
    } else if (!walletId && walletOptions.length === 1) {
      setWalletId(walletOptions[0].id);
    }
  }, [
    application.applicationWallet,
    nominatedWalletId,
    walletId,
    walletOptions,
  ]);

  useEffect(() => {
    loadCashflows();
  }, [walletId]);

  const existingForApplication = cashflows.filter(
    (cashflow) =>
      cashflow.creditApplicationRef?.applicationId === String(application.id),
  );
  const customerUserId = lshApplicationApplicantUserId(application);
  const linkedWalletId =
    lshApplicationLinkedWalletId(application) ||
    (existingForApplication.length ? walletId : undefined);
  const walletDetailPath =
    customerUserId && linkedWalletId
      ? `/customers/dashboard/${customerUserId}/wallets/${linkedWalletId}`
      : '';
  const walletDetailState =
    selectedWallet && linkedWalletId
      ? {
          wallets: [
            {
              id: linkedWalletId,
              name:
                selectedWallet.name ||
                selectedWallet.accountName ||
                'Linked wallet',
              accountNumber: selectedWallet.accountNumber || null,
              balance: Number(selectedWallet.availableBalance || 0),
              accountType: EGPAY_ACCOUNT_TYPE.TPFM,
            },
          ],
          selectedWalletId: linkedWalletId,
        }
      : undefined;
  const walletControlsDisabled = loadingWallets;
  const canCreateDraft =
    Boolean(walletId) && !walletControlsDisabled && isApprovedStage;

  return (
    <div className="application-approved-cashflow-card">
      <Typography variant="h6">Approved cashflow &amp; wallet</Typography>
      <Typography variant="body2" color="textSecondary">
        Submitted cashflow lives in the financials journey. After approval, link
        the farmer wallet and publish an approved cashflow budget for spend
        tracking.
      </Typography>

      {loadingWallets ? (
        <div className="application-approved-cashflow-card__loading">
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Loading farmer wallets...
          </Typography>
        </div>
      ) : walletOptions.length ? (
        <div className="application-approved-cashflow-card__row">
          <TextField
            select
            label="Farmer wallet"
            variant="outlined"
            size="small"
            value={walletId}
            onChange={(event) => setWalletId(event.target.value)}
            helperText={
              selectedWallet
                ? `${selectedWallet.accountName || 'Wallet account'}${
                    selectedWallet.accountNumber
                      ? ` - ${selectedWallet.accountNumber}`
                      : ''
                  }`
                : 'Select a linked farmer wallet'
            }
          >
            <MenuItem value="">Select wallet</MenuItem>
            {walletOptions.map((wallet) => (
              <MenuItem key={wallet.id} value={wallet.id}>
                {wallet.name} - R{lshFormatMoney(wallet.availableBalance)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            color="primary"
            disabled={!canCreateDraft}
            onClick={() => setDraftOpen(true)}
          >
            Review approved cashflow
          </Button>
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No linked farmer wallets found.
        </Typography>
      )}

      {!isApprovedStage ? (
        <Typography variant="caption" color="textSecondary">
          Move the application to an approved-stage state before creating the
          approved cashflow.
        </Typography>
      ) : null}

      {loadingCashflows ? <CircularProgress size={22} /> : null}
      {message ? (
        <Typography variant="body2" style={{color: '#2e7d32'}}>
          {message}
        </Typography>
      ) : null}
      {existingForApplication.length ? (
        <div className="application-approved-cashflow-card__list">
          <div className="application-approved-cashflow-card__list-header">
            <Typography variant="subtitle2">Approved cashflows</Typography>
            {walletDetailPath ? (
              <Link
                className="application-summary-card__link"
                to={{
                  pathname: walletDetailPath,
                  state: walletDetailState,
                }}
              >
                View wallet
              </Link>
            ) : null}
          </div>
          {existingForApplication.map((cashflow) => (
            <div
              key={cashflow.id}
              className="application-approved-cashflow-card__item"
            >
              <span>
                Linked {cashflow.linkedAt || '-'} - R
                {lshFormatMoney(cashflow.totalAllocatedAmount)}
              </span>
              <span>{cashflow.budgets?.length || 0} budget line(s)</span>
            </div>
          ))}
        </div>
      ) : null}

      <Typography variant="caption" color="textSecondary">
        Funder signed documents: attach via application documents (Agreement /
        supporting docs). Budget audit history is exposed when the backend audit
        query is available on GraphQL.
      </Typography>

      <ApplicationApprovedCashflowReviewDialog
        application={application}
        open={draftOpen}
        defaultWalletId={walletId}
        onClose={() => setDraftOpen(false)}
        onPublished={(publishedWalletId) => {
          setMessage(
            'Approved cashflow created from submitted application budgets.',
          );
          setWalletId(publishedWalletId);
          loadCashflows(publishedWalletId);
          onLinked?.();
        }}
      />
    </div>
  );
}

export function ApplicationApprovedCashflowReviewDialog(
  props: ApplicationApprovedCashflowReviewDialogProps,
) {
  const {
    application,
    open,
    defaultWalletId,
    defaultWalletOption,
    lockWalletSelection = false,
    confirmLabel = 'Create approved cashflow',
    onClose,
    onPublished,
  } = props;
  const [walletId, setWalletId] = useState(defaultWalletId || '');
  const [walletOptions, setWalletOptions] = useState<
    TApplicationWalletOption[]
  >([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [cashflows, setCashflows] = useState<IWalletCashflow[]>([]);
  const [loadingCashflows, setLoadingCashflows] = useState(false);
  const [preparingDraft, setPreparingDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<IApprovedCashflowDraft>();
  const [error, setError] = useState('');
  const [resultDialog, setResultDialog] =
    useState<TApprovedCashflowResultDialog | null>(null);

  const walletOptionsForSelect = useMemo(
    () => lshMergeWalletOptions(walletOptions, defaultWalletOption || null),
    [defaultWalletOption, walletOptions],
  );
  const selectedWallet = useMemo(
    () => walletOptionsForSelect.find((wallet) => wallet.id === walletId),
    [walletId, walletOptionsForSelect],
  );
  const draftRows = useMemo(
    () => draft?.linesByMonth.filter((month) => month.lines.length) || [],
    [draft],
  );
  const draftTableRows = useMemo<TApprovedCashflowTableRow[]>(() => {
    const rows = new Map<string, TApprovedCashflowTableRow>();

    draft?.linesByMonth.forEach((month) => {
      month.lines.forEach((line) => {
        const row =
          rows.get(line.spendCategoryId) ||
          ({
            id: line.spendCategoryId,
            label: line.spendCategoryLabel,
            total: 0,
            values: {},
          } as TApprovedCashflowTableRow);

        const currentAmount = Number(row.values[month.key] || 0);
        const lineAmount = Number(line.allocatedAmount || 0);
        row.values[month.key] = currentAmount + lineAmount;
        row.total += lineAmount;
        rows.set(line.spendCategoryId, row);
      });
    });

    return Array.from(rows.values()).sort((left, right) =>
      left.label.localeCompare(right.label),
    );
  }, [draft]);
  const draftMonthTotals = useMemo(() => {
    return (draft?.months || []).reduce<Record<string, number>>(
      (totals, month) => {
        totals[month.key] = draftTableRows.reduce(
          (sum, row) => sum + Number(row.values[month.key] || 0),
          0,
        );
        return totals;
      },
      {},
    );
  }, [draft?.months, draftTableRows]);
  const existingForApplication = useMemo(
    () =>
      cashflows.filter(
        (cashflow) =>
          cashflow.creditApplicationRef?.applicationId ===
          String(application.id),
      ),
    [application.id, cashflows],
  );

  useEffect(() => {
    if (!open) return;
    setWalletId(defaultWalletId || '');
    setDraft(undefined);
    setError('');
  }, [defaultWalletId, open]);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;

    setLoadingWallets(true);
    lshLoadApplicationWalletOptions(application)
      .then((options) => {
        if (!isMounted) return;
        setWalletOptions(options);
        const nextOptions = lshMergeWalletOptions(
          options,
          defaultWalletOption || null,
        );
        if (!defaultWalletId && nextOptions.length === 1) {
          setWalletId(nextOptions[0].id);
        }
      })
      .catch((err) => {
        console.error('Error loading application wallets', err);
        if (isMounted) setWalletOptions([]);
      })
      .finally(() => {
        if (isMounted) setLoadingWallets(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application, defaultWalletId, defaultWalletOption, open]);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;

    setPreparingDraft(true);
    apiGetSpendCategories(true)
      .then((categories) => {
        if (!isMounted) return;
        setDraft(
          lshBuildApprovedCashflowDraft({
            application,
            spendCategories: categories,
          }),
        );
      })
      .catch((err: any) => {
        if (!isMounted) return;
        setError(lshErrorMessage(err, 'Failed to prepare approved cashflow'));
      })
      .finally(() => {
        if (isMounted) setPreparingDraft(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application, open]);

  useEffect(() => {
    if (!open || !walletId) {
      setCashflows([]);
      return;
    }
    let isMounted = true;

    setLoadingCashflows(true);
    apiGetWalletCashflows(walletId)
      .then((rows) => {
        if (isMounted) setCashflows(rows || []);
      })
      .catch((err) => {
        console.error(err);
        if (isMounted) setCashflows([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCashflows(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, walletId]);

  const canConfirm =
    Boolean(walletId) &&
    Boolean(draft) &&
    !saving &&
    !preparingDraft &&
    !loadingWallets &&
    !draft?.requiresPeriodSelection &&
    !draft?.unmappedItems.length &&
    lshDraftHasBudgetLines(draft);

  const handleConfirm = async () => {
    if (!walletId || !draft || !canConfirm) return;

    setSaving(true);
    setError('');
    try {
      await lshPublishApprovedCashflow(application, walletId);
      setResultDialog({
        type: 'success',
        title: 'Approved cashflow linked',
        message: `Application #${application.id} was linked to ${
          selectedWallet?.name || 'the selected wallet'
        }.`,
        walletId,
      });
    } catch (err: any) {
      const message = lshErrorMessage(
        err,
        'Failed to create approved cashflow',
      );
      setError(message);
      setResultDialog({
        type: 'error',
        title: 'Cashflow was not linked',
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResultClose = async () => {
    const currentResult = resultDialog;
    setResultDialog(null);

    if (currentResult?.type !== 'success' || !currentResult.walletId) return;

    try {
      await onPublished?.(currentResult.walletId);
      onClose();
    } catch (err: any) {
      const message = lshErrorMessage(
        err,
        'The approved cashflow was linked, but the page could not finish refreshing.',
      );
      setError(message);
      setResultDialog({
        type: 'error',
        title: 'Linked, but refresh failed',
        message,
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !saving && onClose()}
        maxWidth={false}
        fullWidth
        PaperProps={{className: 'application-approved-cashflow-dialog'}}
      >
        <DialogTitle>Create approved cashflow</DialogTitle>
        <DialogContent dividers>
          <div className="application-approved-cashflow-card__draft">
            {loadingWallets ? (
              <div className="application-approved-cashflow-card__loading">
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">
                  Loading farmer wallets...
                </Typography>
              </div>
            ) : walletOptionsForSelect.length ? (
              <TextField
                select
                label="Farmer wallet"
                variant="outlined"
                size="small"
                value={walletId}
                disabled={lockWalletSelection}
                onChange={(event) => setWalletId(event.target.value)}
                helperText={
                  selectedWallet
                    ? `${selectedWallet.accountName || 'Wallet account'}${
                        selectedWallet.accountNumber
                          ? ` - ${selectedWallet.accountNumber}`
                          : ''
                      }`
                    : 'Select a linked farmer wallet'
                }
              >
                <MenuItem value="">Select wallet</MenuItem>
                {walletOptionsForSelect.map((wallet) => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    {wallet.name} - R{lshFormatMoney(wallet.availableBalance)}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body2" color="error">
                No linked farmer wallets found.
              </Typography>
            )}

            {loadingCashflows ? <CircularProgress size={18} /> : null}

            {existingForApplication.length ? (
              <div className="application-approved-cashflow-card__list">
                <Typography variant="subtitle2">
                  Existing approved cashflow for this wallet
                </Typography>
                {existingForApplication.map((cashflow) => (
                  <div
                    key={cashflow.id}
                    className="application-approved-cashflow-card__item"
                  >
                    <span>
                      Linked {cashflow.linkedAt || '-'} - R
                      {lshFormatMoney(cashflow.totalAllocatedAmount)}
                    </span>
                    <span>{cashflow.budgets?.length || 0} budget line(s)</span>
                  </div>
                ))}
              </div>
            ) : null}

            {preparingDraft ? (
              <div className="application-approved-cashflow-card__loading">
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">
                  Preparing approved cashflow...
                </Typography>
              </div>
            ) : draft ? (
              <>
                <div className="application-approved-cashflow-card__draft-summary">
                  <Typography variant="body2">
                    Approved total: R
                    {lshFormatMoney(draft.totalAllocatedAmount)}
                  </Typography>
                  <Typography variant="body2">
                    Funding ask: R{lshFormatMoney(draft.fundingAskTotal)}
                  </Typography>
                  <Typography variant="body2">
                    Difference: R
                    {lshFormatMoney(draft.reconciliationDifference)}
                  </Typography>
                </div>

                {draft.periodStart && draft.periodEnd ? (
                  <Typography variant="body2" color="textSecondary">
                    Period {draft.periodStart} to {draft.periodEnd}
                  </Typography>
                ) : null}

                {draft.requiresPeriodSelection ? (
                  <Typography variant="body2" color="error">
                    Select a cashflow period before publishing approved budgets.
                  </Typography>
                ) : null}

                {!lshDraftHasBudgetLines(draft) ? (
                  <Typography variant="body2" color="error">
                    No approved budget lines could be created from this
                    application cashflow.
                  </Typography>
                ) : null}

                {draft.unmappedItems.length ? (
                  <div className="application-approved-cashflow-card__draft-warning">
                    <Typography variant="subtitle2" color="error">
                      Unmapped spend categories
                    </Typography>
                    {draft.unmappedItems.map((item, index) => (
                      <Typography
                        key={`${item.label}-${index}`}
                        variant="body2"
                      >
                        {item.label} - R{lshFormatMoney(item.amount)}
                      </Typography>
                    ))}
                  </div>
                ) : null}

                {draft.warnings.length ? (
                  <div className="application-approved-cashflow-card__draft-warning">
                    {draft.warnings.map((warning) => (
                      <Typography key={warning} variant="body2">
                        {warning}
                      </Typography>
                    ))}
                  </div>
                ) : null}

                {draftRows.length ? (
                  <div className="application-approved-cashflow-card__draft-table-scroll">
                    <table className="application-approved-cashflow-card__draft-table">
                      <thead>
                        <tr>
                          <th scope="col">Cost item</th>
                          {draft.months.map((month) => (
                            <th key={month.key} scope="col">
                              {month.label}
                            </th>
                          ))}
                          <th scope="col">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draftTableRows.map((row) => (
                          <tr key={row.id}>
                            <th scope="row">{row.label}</th>
                            {draft.months.map((month) => {
                              const amount = Number(row.values[month.key] || 0);
                              return (
                                <td key={`${row.id}-${month.key}`}>
                                  {amount ? `R${lshFormatMoney(amount)}` : '-'}
                                </td>
                              );
                            })}
                            <td>R{lshFormatMoney(row.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th scope="row">Total</th>
                          {draft.months.map((month) => (
                            <td key={`total-${month.key}`}>
                              {draftMonthTotals[month.key]
                                ? `R${lshFormatMoney(
                                    draftMonthTotals[month.key],
                                  )}`
                                : '-'}
                            </td>
                          ))}
                          <td>R{lshFormatMoney(draft.totalAllocatedAmount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : null}
              </>
            ) : null}

            {error ? (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {saving ? 'Creating...' : confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(resultDialog)}
        onClose={handleResultClose}
        maxWidth="xs"
        fullWidth
      >
        {resultDialog ? (
          <DialogContent>
            <div
              className={[
                'application-approved-cashflow-result',
                `application-approved-cashflow-result--${resultDialog.type}`,
              ].join(' ')}
            >
              <span className="application-approved-cashflow-result__mark" />
              <Typography variant="h6">{resultDialog.title}</Typography>
              <Typography variant="body2" color="textSecondary">
                {resultDialog.message}
              </Typography>
            </div>
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={handleResultClose}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ApplicationApprovedCashflowCard;
